'use client'

import * as React from 'react'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { CURRENT_PRIVACY_POLICY_VERSION } from '@/lib/config'
import { DialogProps } from '@/types/types'
import { cn } from '@/lib/utils/utils'

const dialogSurfaceClassName =
    'flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden border border-slate-200/90 bg-background p-0 shadow-lg max-md:fixed max-md:inset-0 max-md:left-0 max-md:top-0 max-md:h-dvh max-md:max-h-dvh max-md:w-full max-md:max-w-full max-md:translate-x-0 max-md:translate-y-0 max-md:rounded-none max-md:border-0 max-md:data-[state=open]:zoom-in-100 max-md:data-[state=closed]:zoom-out-100 max-md:data-[state=open]:slide-in-from-bottom-0 max-md:data-[state=closed]:slide-out-to-bottom-0 sm:max-w-3xl sm:rounded-4xl sm:border sm:border-slate-200/90'

export function PrivacyPolicyDialog({ labelColor }: DialogProps) {
    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button
                    className={cn(
                        'cursor-pointer px-0 py-0 hover:no-underline focus-visible:ring-0 focus-visible:ring-offset-0',
                        labelColor && `text-${labelColor}`
                    )}
                    variant='link'
                    style={{ color: labelColor }}>
                    Privacy Policy
                </Button>
            </DialogTrigger>
            <DialogContent className={dialogSurfaceClassName}>
                <DialogDescription className='sr-only'>
                    How myworldesk collects, uses, and protects personal data when you use our website and services.
                </DialogDescription>
                <div className='flex min-h-0 flex-1 flex-col'>
                    <div className='shrink-0 border-b border-border/40 px-8 pb-7 pt-8 pr-14 max-md:pt-[max(2rem,env(safe-area-inset-top))] sm:px-10 sm:pb-8 sm:pt-10 sm:pr-16'>
                        <DialogHeader className='space-y-4 text-left sm:space-y-5'>
                            <span className='text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-petrol'>
                                Legal
                            </span>
                            <DialogTitle className='text-2xl font-extrabold leading-tight tracking-[-0.03em] text-darkgrey [font-family:var(--font-newblack),var(--font-poppins),system-ui,sans-serif] sm:text-3xl'>
                                Privacy Policy
                            </DialogTitle>
                        </DialogHeader>
                        <p className='mt-5 max-w-prose text-sm leading-relaxed text-muted-foreground sm:mt-6 sm:text-base'>
                            This policy describes how we handle personal information when you use myworldesk. If you
                            have questions, contact us at{' '}
                            <a
                                className='font-medium text-petrol underline decoration-petrol/30 underline-offset-4 transition-colors hover:decoration-petrol'
                                href='mailto:info@myworldesk.com'>
                                info@myworldesk.com
                            </a>
                            .
                        </p>
                    </div>
                    <div className='min-h-0 flex-1 overflow-y-auto overscroll-contain px-8 py-8 sm:px-10 sm:py-10'>
                        <div className='mx-auto max-w-prose space-y-10 text-base leading-relaxed text-muted-foreground sm:space-y-12'>
                            <section className='space-y-4'>
                                <h3 className='text-sm font-semibold text-foreground'>Overview</h3>
                                <p>
                                    We respect your privacy. This document explains what personal data we may collect,
                                    why we process it, and the choices you have. It applies to our website,
                                    applications, and related services operated by myworldesk OÜ.
                                </p>
                            </section>
                            <section className='space-y-4'>
                                <h3 className='text-sm font-semibold text-foreground'>Data we process</h3>
                                <p>
                                    Depending on how you use myworldesk, we may process account and profile details,
                                    contact information, usage and device data, communications you send us, and
                                    information needed to provide bookings, payments, or support. We aim to limit
                                    collection to what is necessary for the relevant purpose.
                                </p>
                            </section>
                            <section className='space-y-4'>
                                <h3 className='text-sm font-semibold text-foreground'>How we use data</h3>
                                <p>
                                    We use personal data to run and improve the service, authenticate users, process
                                    transactions, communicate with you, comply with legal obligations, and protect
                                    against abuse or fraud. We do not sell your personal information.
                                </p>
                            </section>
                            <section className='space-y-4'>
                                <h3 className='text-sm font-semibold text-foreground'>
                                    Cookies and similar technologies
                                </h3>
                                <p>
                                    We may use cookies or similar technologies where needed for security, preferences,
                                    or analytics. You can control cookies through your browser settings; disabling some
                                    cookies may affect how certain features work.
                                </p>
                            </section>
                            <section className='space-y-4'>
                                <h3 className='text-sm font-semibold text-foreground'>Sharing and processors</h3>
                                <p>
                                    We may share data with trusted service providers (for example hosting, email, or
                                    payment processing) under strict agreements, or when required by law. Any transfer
                                    outside your region is done with appropriate safeguards where applicable.
                                </p>
                            </section>
                            <section className='space-y-4'>
                                <h3 className='text-sm font-semibold text-foreground'>Retention and security</h3>
                                <p>
                                    We retain personal data only as long as needed for the purposes described or as
                                    required by law. We apply technical and organizational measures designed to protect
                                    your information, though no online service can guarantee absolute security.
                                </p>
                            </section>
                            <section className='space-y-4'>
                                <h3 className='text-sm font-semibold text-foreground'>Your rights</h3>
                                <p>
                                    Depending on applicable law, you may have the right to access, correct, delete, or
                                    restrict processing of your personal data, to object to certain processing, or to
                                    lodge a complaint with a supervisory authority. Contact us to exercise these rights.
                                </p>
                            </section>
                            <section className='space-y-4'>
                                <h3 className='text-sm font-semibold text-foreground'>Changes</h3>
                                <p>
                                    We may update this policy from time to time. The version number below reflects the
                                    current document. Material changes will be communicated as appropriate (for example
                                    by email or an in-product notice).
                                </p>
                            </section>
                            <p className='border-t border-border/40 pt-8 text-sm text-muted-foreground sm:pt-10'>
                                Version {CURRENT_PRIVACY_POLICY_VERSION}
                            </p>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
